import { RequiredCaption, RequiredMark } from '@/shared/ui/atoms/required-mark'
import type { RefSelectProps as AntRefSelectProps, SelectProps as AntSelectProps } from 'antd'
import { Select as AntSelect, ConfigProvider, Empty } from 'antd'
import {
  type ReactNode,
  forwardRef,
  memo,
  useCallback,
  useDeferredValue,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import './select.css'

// ---------------------------------------------------------------------------
// Status color mapping (for valueKey-based coloring)
// ---------------------------------------------------------------------------
function getStatusColor(valueKey?: string): string | undefined {
  if (!valueKey) return undefined
  const key = valueKey.toLowerCase()
  const colorMap: Record<string, string> = {
    pending_confirmation: '#FC9B0B',
    in_progress: '#FC9B0B',
    blocker: '#FC9B0B',
    pending: '#FC9B0B',
    critical: '#FF3B30',
    open: '#FD6195',
    new: '#FF3B30',
    confirmed: '#00C795',
    approved: '#00C795',
    received: '#00C795',
    completed: '#00C795',
    signed: '#00C795',
    delivered: '#00C795',
    waiting: '#FA00D9',
    in_transit: '#5381FF',
    medium: '#5381FF',
    low: '#34A4FF',
    canceled: '#F36A6A',
    rejected: '#F36A6A',
    delayed: '#F36A6A',
  }
  return colorMap[key] ?? undefined
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------
export interface SelectOption {
  value: string | number
  label: string | ReactNode
  disabled?: boolean
}

interface BaseSelectProps {
  label?: string | null
  placeholder?: string
  value?: string | number | string[] | number[] | null
  defaultValue?: string | number | string[] | number[] | null
  onChange?: (value: any, ...args: any[]) => void
  onSelect?: AntSelectProps['onSelect']
  onClear?: AntSelectProps['onClear']
  height?: string
  width?: string
  suffix?: ReactNode
  prefix?: ReactNode
  status?: string
  required?: boolean
  errorData?: Record<string, string | null | undefined>
  field?: string
  options?: SelectOption[]
  allOption?: boolean
  allOptionLabel?: string
  maxTagCount?: number | 'responsive'
  mode?: 'multiple' | 'tags'
  borderLeft?: string
  readOnly?: boolean
  disabled?: boolean
  valueKey?: string
  allowClear?: boolean
  showSearch?: boolean
  name?: string
  id?: string
  autoComplete?: string
  'aria-label'?: string
  style?: React.CSSProperties
  children?: ReactNode
  virtual?: boolean
  optionRender?: AntSelectProps['optionRender']
  error?: string
  className?: string
}

// ---------------------------------------------------------------------------
// Select (optimized with deferred search, caching, virtual scroll)
// ---------------------------------------------------------------------------
export interface SelectProps extends BaseSelectProps {
  disableSort?: boolean
}

const SelectInner = forwardRef<AntRefSelectProps, SelectProps>(function SelectInner(
  {
    label,
    placeholder,
    value,
    onChange,
    height = '40px',
    width = '100%',
    suffix,
    prefix,
    status,
    required,
    errorData,
    field,
    options = [],
    allOption,
    allOptionLabel,
    maxTagCount,
    mode,
    onClear,
    borderLeft,
    readOnly,
    disabled,
    valueKey,
    showSearch = true,
    onSelect,
    disableSort = false,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation()
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const [searchValue, setSearchValue] = useState('')
  const sortedCacheRef = useRef(new Map<string, SelectOption[]>())

  const deferredSearch = useDeferredValue(searchValue)
  const handleSearch = useCallback((val: string) => setSearchValue(val), [])

  const statusColor = getStatusColor(valueKey)

  // Sort options (skip for large lists or when sorting disabled)
  const sortedOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return []
    if (disableSort || options.length > 100) return options

    const cacheKey = JSON.stringify(options.map((o) => o.value).slice(0, 10))
    if (sortedCacheRef.current.has(cacheKey)) {
      return sortedCacheRef.current.get(cacheKey)!
    }

    const sorted = [...options].sort((a, b) => {
      const labelA = String(a?.label ?? '')
      const labelB = String(b?.label ?? '')
      return labelA.localeCompare(labelB, undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    })

    sortedCacheRef.current.set(cacheKey, sorted)
    return sorted
  }, [options, disableSort])

  // Filter based on deferred search
  const filteredOptions = useMemo(() => {
    if (!deferredSearch) return sortedOptions
    const search = deferredSearch.toLowerCase()
    return sortedOptions.filter((option) =>
      String(option?.label ?? '')
        .toLowerCase()
        .includes(search),
    )
  }, [sortedOptions, deferredSearch])

  // Add "all" option if needed
  const finalOptions = useMemo(() => {
    return allOption
      ? [{ value: '' as string | number, label: allOptionLabel ?? t('all') }, ...filteredOptions]
      : filteredOptions
  }, [allOption, allOptionLabel, filteredOptions, t])

  return (
    <div className='flex flex-col gap-2 relative w-full w-(width)'>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      <ConfigProvider
        theme={{
          components: {
            Select: {
              borderRadius: 0,
            },
          },
        }}
      >
        <AntSelect
          ref={ref}
          id={id}
          {...({
            name: name ?? id,
            autoComplete,
          } as { name?: string; autoComplete?: string })}
          aria-label={ariaLabel ?? label ?? undefined}
          className='glasspro-select'
          showSearch={
            showSearch
              ? {
                  filterOption: false,
                  onSearch: handleSearch,
                }
              : false
          }
          styles={{
            root: {
              width,
              height,
              borderLeft: borderLeft || undefined,
              borderColor: statusColor,
              color: statusColor,
              borderRadius: '0px !important',
            },
          }}
          options={finalOptions}
          allowClear
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onSelect={onSelect}
          onClear={onClear}
          suffix={suffix}
          prefix={prefix}
          status={status as AntSelectProps['status']}
          mode={mode}
          maxTagCount={maxTagCount}
          popupMatchSelectWidth={false}
          disabled={disabled || readOnly}
          virtual
          listHeight={320}
          notFoundContent={
            <div className='py-10 px-5 text-center'>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span className='text-[13px] text-gray-400'>{t('no_data')}</span>}
              />
            </div>
          }
          {...rest}
        />
      </ConfigProvider>
      {status === 'error' && errorData && (
        <RequiredCaption errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})

// Custom memo comparison for performance
function areSelectPropsEqual(prev: SelectProps, next: SelectProps): boolean {
  if (
    prev.value !== next.value ||
    prev.status !== next.status ||
    prev.disabled !== next.disabled ||
    prev.readOnly !== next.readOnly ||
    prev.width !== next.width ||
    prev.label !== next.label ||
    prev.placeholder !== next.placeholder ||
    prev.allOption !== next.allOption ||
    prev.allOptionLabel !== next.allOptionLabel ||
    prev.onChange !== next.onChange
  ) {
    return false
  }

  if (prev.options !== next.options) {
    if (!prev.options || !next.options || prev.options.length !== next.options.length) {
      return false
    }
    for (let i = 0; i < prev.options.length; i++) {
      if (
        prev.options[i]?.value !== next.options[i]?.value ||
        prev.options[i]?.label !== next.options[i]?.label
      ) {
        return false
      }
    }
  }

  if (prev.errorData !== next.errorData) {
    if (!prev.errorData || !next.errorData) return prev.errorData === next.errorData
    const prevKeys = Object.keys(prev.errorData)
    const nextKeys = Object.keys(next.errorData)
    if (prevKeys.length !== nextKeys.length) return false
    for (const key of prevKeys) {
      if (prev.errorData[key] !== next.errorData[key]) return false
    }
  }

  return true
}

export const Select = memo(SelectInner, areSelectPropsEqual)

// ---------------------------------------------------------------------------
// SimpleSelect (no search optimization, basic usage)
// ---------------------------------------------------------------------------
export interface SimpleSelectProps extends BaseSelectProps {}

export const SimpleSelect = forwardRef<AntRefSelectProps, SimpleSelectProps>(function SimpleSelect(
  {
    label,
    placeholder,
    value,
    onChange,
    height = '40px',
    width = '100%',
    suffix,
    prefix,
    status,
    required,
    errorData,
    field,
    options = [],
    allOption,
    allOptionLabel,
    maxTagCount,
    mode,
    onClear,
    borderLeft,
    readOnly,
    disabled,
    valueKey,
    showSearch = true,
    allowClear = false,
    onSelect,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation()
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const statusColor = getStatusColor(valueKey)

  const finalOptions = useMemo(() => {
    return allOption
      ? [{ value: '' as string | number, label: allOptionLabel ?? t('all') }, ...options]
      : options
  }, [allOption, allOptionLabel, options, t])

  return (
    <div className='flex flex-col gap-2 relative w-full w-(width)'>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      <ConfigProvider
        theme={{
          components: {
            Select: {
              borderRadius: 0,
            },
          },
        }}
      >
        <AntSelect
          ref={ref}
          id={id}
          {...({
            name: name ?? id,
            autoComplete,
          } as { name?: string; autoComplete?: string })}
          aria-label={ariaLabel ?? label ?? undefined}
          className='glasspro-select'
          showSearch={showSearch ? { filterOption: false } : false}
          styles={{
            root: {
              width,
              height,
              borderLeft: borderLeft || undefined,
              borderColor: statusColor,
              color: statusColor,
              borderRadius: '0px !important',
            },
          }}
          options={finalOptions}
          allowClear={allowClear}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onSelect={onSelect}
          onClear={onClear}
          suffix={suffix}
          prefix={prefix}
          status={status as AntSelectProps['status']}
          mode={mode}
          maxTagCount={maxTagCount}
          popupMatchSelectWidth={false}
          disabled={disabled || readOnly}
          notFoundContent={
            <div className='py-10 px-5 text-center'>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span className='text-[13px] text-gray-400'>{t('no_data')}</span>}
              />
            </div>
          }
          {...rest}
        />
      </ConfigProvider>
      {status === 'error' && errorData && (
        <RequiredCaption errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})

// ---------------------------------------------------------------------------
// CustomSelect (with built-in filter + sort via Ant Design)
// ---------------------------------------------------------------------------
export interface CustomSelectProps extends BaseSelectProps {
  disableSort?: boolean
}

export const CustomSelect = forwardRef<AntRefSelectProps, CustomSelectProps>(function CustomSelect(
  {
    label,
    placeholder,
    value,
    onChange,
    height = '40px',
    width = '100%',
    suffix,
    prefix,
    status,
    required,
    errorData,
    field,
    options = [],
    allOption,
    allOptionLabel,
    maxTagCount,
    mode,
    onClear,
    borderLeft,
    readOnly,
    disabled,
    valueKey,
    showSearch = true,
    allowClear = true,
    onSelect,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation()
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  const statusColor = getStatusColor(valueKey)

  const finalOptions = useMemo(() => {
    return allOption
      ? [{ value: '' as string | number, label: allOptionLabel ?? t('all') }, ...options]
      : options
  }, [allOption, allOptionLabel, options, t])

  return (
    <div className='flex flex-col gap-2 relative w-full w-(width)'>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      <ConfigProvider
        theme={{
          components: {
            Select: {
              borderRadius: 0,
            },
          },
        }}
      >
        <AntSelect
          ref={ref}
          id={id}
          {...({
            name: name ?? id,
            autoComplete,
          } as { name?: string; autoComplete?: string })}
          aria-label={ariaLabel ?? label ?? undefined}
          className='glasspro-select'
          showSearch={
            showSearch
              ? {
                  optionFilterProp: 'label',
                  filterOption: (input, option) => {
                    if (!input) return true
                    const optLabel = String(option?.label ?? '')
                    return optLabel.toLowerCase().includes(input.toLowerCase())
                  },
                  filterSort: (optionA, optionB) => {
                    // Pin the "all / not-selected" option (value === '') to the top
                    if (optionA?.value === '') return -1
                    if (optionB?.value === '') return 1
                    const labelA = String(optionA?.label ?? '').toLowerCase()
                    const labelB = String(optionB?.label ?? '').toLowerCase()
                    return labelA.localeCompare(labelB)
                  },
                }
              : false
          }
          styles={{
            root: {
              width,
              height,
              borderLeft: borderLeft || undefined,
              borderColor: statusColor,
              color: statusColor,
              borderRadius: '0px !important',
            },
          }}
          options={finalOptions}
          allowClear={allowClear}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onSelect={onSelect}
          onClear={onClear}
          suffix={suffix}
          prefix={prefix}
          status={status as AntSelectProps['status']}
          mode={mode}
          maxTagCount={maxTagCount}
          popupMatchSelectWidth={false}
          disabled={disabled || readOnly}
          notFoundContent={
            <div className='py-10 px-5 text-center'>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span className='text-[13px] text-gray-400'>{t('no_data')}</span>}
              />
            </div>
          }
          {...rest}
        />
      </ConfigProvider>
      {status === 'error' && errorData && (
        <RequiredCaption errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})
